import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";

export default function AdminAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <ToolPageShell
      eyebrow="Admin · Analytics"
      title="Operational intelligence center"
      intro="Aggregate program utilization, vendor network health, workflow bottlenecks, and platform-wide adoption."
    >
      {!data ? (
        <div className="text-sm text-slate-500">Loading analytics…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
            <Kpi label="Active clinics" v={data.summary.active_clinics} accent="bg-blue-600" />
            <Kpi label="Vendors" v={data.summary.vendor_count} accent="bg-violet-600" />
            <Kpi label="Workflows" v={data.summary.total_workflows} accent="bg-indigo-600" />
            <Kpi label="Paid invoices" v={data.summary.paid_invoices} accent="bg-emerald-600" />
            <Kpi label="Platform revenue" v={`$${data.summary.platform_revenue.toLocaleString()}`} accent="bg-amber-600" />
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mb-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                Workflow timeline (last 6 months)
              </div>
              {data.timeline.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No data yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={data.timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="new" name="New" stroke="#2563eb" fill="#2563eb" fillOpacity={0.18} />
                    <Area type="monotone" dataKey="completed" name="Completed" stroke="#16a34a" fill="#16a34a" fillOpacity={0.18} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                Workflow bottlenecks
              </div>
              {data.bottlenecks.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No data.</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.bottlenecks}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="status" tick={{ fontSize: 9 }} angle={-15} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#d97706" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mb-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                Program-category roll-up
              </div>
              {data.categories.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No category data yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.categories}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total" name="Workflows" fill="#2563eb" radius={[5, 5, 0, 0]} />
                    <Bar dataKey="completed" name="Completed" fill="#16a34a" radius={[5, 5, 0, 0]} />
                    <Bar dataKey="paid" name="Paid" fill="#d97706" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                Vendor network workload
              </div>
              <div className="overflow-y-auto max-h-[260px] -mx-2">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 px-2">Vendor</th>
                      <th className="py-2 px-2">Category</th>
                      <th className="py-2 px-2 text-right">Workflows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.vendor_network.map((v) => (
                      <tr key={v.vendor_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-2 font-semibold text-slate-900 truncate max-w-[180px]">{v.vendor_name}</td>
                        <td className="py-2 px-2 text-slate-700 capitalize">{v.category || "—"}</td>
                        <td className="py-2 px-2 text-right font-mono">{v.workflows}</td>
                      </tr>
                    ))}
                    {data.vendor_network.length === 0 && (
                      <tr><td colSpan={3} className="text-sm text-slate-500 py-6 text-center">No vendors registered.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-[11px] text-slate-500 leading-relaxed">
            <b>Compliance:</b> All metrics here are operational and engagement-based. No clinical outcomes,
            diagnoses, or treatment-quality measures are aggregated or surfaced.
          </div>
        </>
      )}
    </ToolPageShell>
  );
}

function Kpi({ label, v, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`w-1.5 h-6 rounded-full ${accent} mb-2`} />
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className="font-display text-2xl font-extrabold text-slate-900 mt-1">{v}</div>
    </div>
  );
}
